import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { Contact } from "@freightsimple/generated-dashboard-openapi-client/models/Contact";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { SetAddressAndContactOperationRequest } from "@freightsimple/generated-dashboard-openapi-client/apis/ShipmentsApi";
import type { SetAddressAndContactRequest } from "@freightsimple/generated-dashboard-openapi-client/models/SetAddressAndContactRequest";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import { v4 as uuidv4 } from "uuid";
import { useNavigate } from "react-router-dom";
import { useShipmentService } from "@/Services/ShipmentService";
import { useShipmentService2 } from "@/Services/ShipmentService2";
import { useShipmentsApi } from "@/Hooks/useShipmentsApi";
import { useState } from "react";
import { useLineItemsShipmentLimits } from "@/Shared/LineItems/Validators/useLineItemsShipmentLimits";
import { qualifiesForLiftGateLongThinException } from "@/Shared/LineItems/Validators/lineItemsDimensionErrorMessages";

// The requote saves the address screen as it stands, which may be before the
// customer has typed the business name or street address. The API requires both,
// so send them blank rather than missing; the address screen still requires them
// before booking.
function toPartialLocation(location: PreQuoteLocation): Location {
  return {
    ...location,
    businessName: location.businessName ?? "",
    address: {
      ...location.address,
      addressLine: location.address.addressLine ?? "",
    },
  };
}

interface RequoteWithLiftGateSectionProps {
  shipment: PreBookingShipment;
  context: LocationContext;
  locationHasDock: LoadingCapabilitiesAvailability | undefined;
  locationHasForklift: LoadingCapabilitiesAvailability | undefined;
  contact: Contact;
  referenceNumber: string;
  amazonReferenceId: string | undefined;
  amazonShipmentId: string | undefined;
  boothNumber: string;
  deadlineDate: string | undefined;
  markLocationAsFavourite: boolean | undefined;
  isContactNotRequired: boolean;
  savedLocations: SavedLocation[];
  onModify: () => void;
}

function BookShipmentRequoteWithLiftGateSection(
  props: RequoteWithLiftGateSectionProps,
) {
  const {
    shipment,
    context,
    locationHasDock,
    locationHasForklift,
    contact,
    referenceNumber,
    amazonReferenceId,
    amazonShipmentId,
    boothNumber,
    deadlineDate,
    markLocationAsFavourite,
    isContactNotRequired,
  } = props;

  const navigate = useNavigate();
  const shipmentsApi = useShipmentsApi();
  const shipmentsService = useShipmentService();
  const shipmentsService2 = useShipmentService2();
  const [loading, setLoading] = useState(false);
  const [requoteFailed, setRequoteFailed] = useState(false);
  const limits = useLineItemsShipmentLimits();

  const location =
    context === LocationContext.Pickup
      ? shipment.pickupLocation
      : shipment.deliveryLocation;

  const maxLiftGateDimensionInches = limits.liftgate.maxDimensionInches;
  const maxLiftGateWeightPounds =
    limits.liftgate.maxWeightPerHandlingUnitPounds;

  function needsLiftGate(): boolean {
    return (
      locationHasDock === LoadingCapabilitiesAvailability.No &&
      locationHasForklift === LoadingCapabilitiesAvailability.No
    );
  }

  // Long-thin freight is hand-offloaded, so it is exempt from both liftgate caps.
  const liftGateLineItems = shipment.lineItems.filter(
    (li) => !qualifiesForLiftGateLongThinException(li, limits),
  );
  const tooLargeForLiftGate = liftGateLineItems.some(
    (li) =>
      li.length > maxLiftGateDimensionInches ||
      li.width > maxLiftGateDimensionInches ||
      li.height > maxLiftGateDimensionInches,
  );
  // The server refuses to quote these with a lift gate, so offering the requote
  // would only fail.
  const tooHeavyForLiftGate = liftGateLineItems.some(
    (li) => li.weightPerHandlingUnit > maxLiftGateWeightPounds,
  );

  // Half a pair packs into a reference the carrier cannot use; blank-blank is
  // fine mid-flow, the address screen enforces both before booking.
  const amazonIdsHalfFilled =
    ((amazonReferenceId ?? "").trim() === "") !==
    ((amazonShipmentId ?? "").trim() === "");

  function getContactForRequest(): Contact {
    if (isContactNotRequired) {
      return {
        contactName: "",
        phoneNumber: "",
        emailAddress: "",
        phoneNumberExtension: "",
      };
    }
    return contact;
  }

  async function requoteWithLiftGate() {
    setLoading(true);
    setRequoteFailed(false);

    try {
      await requoteWithLiftGateSteps();
    } catch (error) {
      console.error("Error creating shipment with lift gate:", error);
      // Only a step before the replacement exists can land here, and the
      // original is untouched until then, so staying keeps what was entered.
      setRequoteFailed(true);
    } finally {
      setLoading(false);
    }
  }

  // oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
  async function requoteWithLiftGateSteps() {
    const request = {
      setAddressAndContactRequest: {
        shipmentId: shipment.shipmentId,
        context: context,
        location: toPartialLocation(location),
        contact: getContactForRequest(),
        // The ids are only handed in for an Amazon warehouse delivery; the
        // server packs them, so the packed string is not sent alongside them.
        referenceNumber:
          amazonReferenceId !== undefined || amazonShipmentId !== undefined
            ? undefined
            : referenceNumber,
        amazonReferenceId: amazonReferenceId,
        amazonShipmentId: amazonShipmentId,
        boothNumber: boothNumber,
        deadline: deadlineDate,
        markLocationAsFavourite: markLocationAsFavourite,
        markLocationAsDefault: false,
        pickupLocationHasDock:
          context === LocationContext.Pickup ? locationHasDock : undefined,
        pickupLocationHasForklift:
          context === LocationContext.Pickup ? locationHasForklift : undefined,
        deliveryLocationHasDock:
          context === LocationContext.Delivery ? locationHasDock : undefined,
        deliveryLocationHasForklift:
          context === LocationContext.Delivery
            ? locationHasForklift
            : undefined,
        // User may not have added contact info yet
        skipValidation: true,
      },
    } as SetAddressAndContactOperationRequest;
    await shipmentsApi.setAddressAndContact(request);

    const updatedShipmentData = await shipmentsService.getPreBookingShipment(
      shipment.shipmentId,
    );

    const updatedShipment = { ...updatedShipmentData };
    if (context === LocationContext.Pickup) {
      updatedShipment.pickupLocation = {
        ...updatedShipmentData.pickupLocation,
        accessorials: [
          ...updatedShipmentData.pickupLocation.accessorials,
          "LOGISTICS_LIFT_GATE_REQUIRED",
        ],
      };
    } else {
      updatedShipment.deliveryLocation = {
        ...updatedShipmentData.deliveryLocation,
        accessorials: [
          ...updatedShipmentData.deliveryLocation.accessorials,
          "LOGISTICS_LIFT_GATE_REQUIRED",
        ],
      };
    }

    const newShipmentId = uuidv4();

    await shipmentsService2.createShipment(
      newShipmentId,
      updatedShipment.pickupDate,
      updatedShipment.pickupLocation,
      updatedShipment.deliveryLocation,
      updatedShipment.pickupContact,
      updatedShipment.deliveryContact,
      updatedShipment.lineItems,
      updatedShipment.pickupReferenceNumber,
      updatedShipment.deliveryReferenceNumber,
      updatedShipment.deliveryAmazonReferenceId,
      updatedShipment.deliveryAmazonShipmentId,
      updatedShipment.addInsuranceToShipment,
      updatedShipment.insuranceAmount,
      updatedShipment.insuranceCurrency,
      updatedShipment.branchId,
      updatedShipment.deliveryDeadline,
      updatedShipment.pickupDeadline,
      updatedShipment.stateOfGoods ?? StateOfGoods.New,
      updatedShipment.pickupLocationHasDock ??
        LoadingCapabilitiesAvailability.Unknown,
      updatedShipment.pickupLocationHasForklift ??
        LoadingCapabilitiesAvailability.Unknown,
      updatedShipment.deliveryLocationHasDock ??
        LoadingCapabilitiesAvailability.Unknown,
      updatedShipment.deliveryLocationHasForklift ??
        LoadingCapabilitiesAvailability.Unknown,
      // shipmentDraftId — lift-gate requote never has one
      undefined,
      // replacesId — the source shipment we're replacing with a lift-gate version
      shipment.shipmentId,
    );

    const hasPickupCapabilities =
      updatedShipmentData.pickupLocationHasDock !== undefined &&
      updatedShipmentData.pickupLocationHasForklift !== undefined;
    const hasDeliveryCapabilities =
      updatedShipmentData.deliveryLocationHasDock !== undefined &&
      updatedShipmentData.deliveryLocationHasForklift !== undefined;

    // From here the replacement is real: it is being quoted and integrations
    // have been told it replaces the original. A failure must carry on to it
    // rather than report nothing changed, or a retry makes another replacement.
    // This save only copies the dock/forklift answers, which the address
    // screen asks for again if they're missing.
    try {
      if (hasPickupCapabilities || hasDeliveryCapabilities) {
        await shipmentsApi.setAddressAndContact({
          setAddressAndContactRequest: {
            shipmentId: newShipmentId,
            context: LocationContext.Pickup,
            location: toPartialLocation(updatedShipment.pickupLocation),
            contact: updatedShipment.pickupContact || {
              contactName: "",
              phoneNumber: "",
              emailAddress: "",
              phoneNumberExtension: "",
            },
            referenceNumber: updatedShipment.pickupReferenceNumber,
            boothNumber: updatedShipment.pickupBoothNumber || "",
            deadline: updatedShipment.pickupDeadline,
            // Carrying the address onto the replacement shipment says nothing
            // about favourites, so don't send an opinion either way.
            markLocationAsDefault: false,
            pickupLocationHasDock: hasPickupCapabilities
              ? updatedShipmentData.pickupLocationHasDock
              : undefined,
            pickupLocationHasForklift: hasPickupCapabilities
              ? updatedShipmentData.pickupLocationHasForklift
              : undefined,
            deliveryLocationHasDock: hasDeliveryCapabilities
              ? updatedShipmentData.deliveryLocationHasDock
              : undefined,
            deliveryLocationHasForklift: hasDeliveryCapabilities
              ? updatedShipmentData.deliveryLocationHasForklift
              : undefined,
            // User may not have added contact info yet
            skipValidation: true,
          } as SetAddressAndContactRequest,
        });
      }
    } catch (error) {
      console.error(
        "Error copying loading capabilities to the lift gate shipment:",
        error,
      );
    }

    // Only now that the replacement exists: deleting first would leave the
    // customer with nothing if creating it failed.
    await shipmentsService.deleteQuotedShipment(
      shipment.shipmentId,
      "Modified to add lift gate due to no dock and no forklift",
    );

    localStorage.setItem("requotedWithLiftGate", "true");

    // Navigate directly to quotes page
    navigate(`/book/details?shipmentId=${newShipmentId}`, {
      replace: true,
    });
    navigate(`/book/quotes?shipmentId=${newShipmentId}`);
  }

  // Only render if lift gate is needed
  if (!needsLiftGate()) {
    return <></>;
  }

  const cannotUseLiftGate = tooLargeForLiftGate || tooHeavyForLiftGate;

  return (
    <div style={{ width: "545px" }}>
      <Banner
        variant="danger"
        hideIcon
        title={
          tooLargeForLiftGate
            ? "Freight too large for a lift gate"
            : tooHeavyForLiftGate
              ? "Freight too heavy for a lift gate"
              : "Lift gate required"
        }
      >
        <Stack gap={2} align="start">
          {cannotUseLiftGate ? (
            <>
              {tooLargeForLiftGate ? (
                <span>
                  This location requires a lift gate service since there is no
                  dock or forklift available. However, your freight has
                  dimensions that exceed {maxLiftGateDimensionInches} inches,
                  which is too large for a lift gate. Please change the shipment
                  details to reduce the dimensions and add a liftgate, or select
                  a location that has a shipping dock or forklift.
                </span>
              ) : (
                <span>
                  This location requires a lift gate service since there is no
                  dock or forklift available. However, your freight has a
                  handling unit over {maxLiftGateWeightPounds} lb, which is too
                  heavy for a lift gate. Please change the shipment details to
                  reduce the weight per handling unit and add a liftgate, or
                  select a location that has a shipping dock or forklift.
                </span>
              )}
              <Button size="link" onClick={props.onModify}>
                Go back to details
              </Button>
            </>
          ) : (
            <>
              <span>
                This location requires a lift gate service since there is no
                dock or forklift available. Click below to requote with lift
                gate.
              </span>
              {requoteFailed && (
                <span role="alert">
                  We couldn&apos;t requote with a lift gate. Your shipment
                  hasn&apos;t changed. Please try again.
                </span>
              )}
              {/* Button owns the spinner and the busy label while requoting. */}
              {amazonIdsHalfFilled ? (
                <Button
                  size="link"
                  disabled
                  disabledMessage="Supply both Amazon ids, or leave both blank"
                >
                  Requote with Lift Gate
                </Button>
              ) : (
                <Button
                  size="link"
                  loading={loading}
                  onClick={requoteWithLiftGate}
                >
                  {loading ? "Requoting..." : "Requote with Lift Gate"}
                </Button>
              )}
            </>
          )}
        </Stack>
      </Banner>
    </div>
  );
}

export default BookShipmentRequoteWithLiftGateSection;
